import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import {
  Autocomplete,
  Button,
  Button as MUIButton,
  Popper,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import { toaster } from '../../services/toaster';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import api from '../../services/api';
import { address } from '../../schema/address';
import { venue } from '../../schema/venue';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import { MoreOptions } from '../Table/assets/MoreOptions';
import { MapGoogle } from '../Map/MapGoogle';
import PersonIcon from '@mui/icons-material/Person';
import RoomIcon from '@mui/icons-material/Room';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import LargePopper from '../Table/assets/LargePopper';

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const Venues = ({
  schema,
  values,
}: {
  schema: any;
  values: any;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>([]);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState<any>(
    undefined
  );
  const [serviceProviderOptions, setServiceProviderOptions] = useState<any>([]);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = React.useState<
    any
  >({
    schema: schema,
    row: {},
  });

  const fetchVenues = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({
      activity: values?.id,
    });
    if (selectedServiceProvider) {
      searchParams.append('service_provider', selectedServiceProvider?.id);
    }
    api.get('/activity-venues', searchParams).then((res: any) => {
      setProperties(res);
      setLoading(false);
    });
  }, [selectedServiceProvider, serviceProviderOptions]);

  const fetchServiceProviders = useCallback(() => {
    const searchParams = new URLSearchParams({
      activity: values?.id,
    });

    api.get('/activity-service-providers', searchParams).then((res: any) => {
      setServiceProviderOptions(res);
    });
  }, []);

  const CustomPopper = function (props: any) {
    return (
      <>
        <div>
          <Popper
            {...props}
            sx={{
              '& .MuiPaper-root': {
                boxShadow: '0 4px 10px -1px rgb(17 20 24 / 20%)',
              },
            }}
            style={{
              width: 'fit-content',
              maxWidth: '300px',
            }}
            placement="bottom-start"
          />
        </div>
      </>
    );
  };

  useEffect(() => {
    fetchServiceProviders();
    fetchVenues();
  }, []);

  useEffect(() => {
    fetchVenues();
  }, [selectedServiceProvider]);

  return (
    <>
      <Stack width={'50%'}>
        <Stack direction={'row'}>
          <Button
            sx={{
              marginY: '20px',
            }}
            variant="contained"
            onClick={() => {
              setCurrentAddNewPaneEntity({
                row: {
                  id: 'new',
                  activity: {
                    id: values?.id,
                    name: values?.name,
                  },
                  service_provider: {
                    id: selectedServiceProvider?.id,
                    name: selectedServiceProvider?.name,
                  },
                },
                schema: venue,
              });
            }}
          >
            Add New Venue
          </Button>
        </Stack>
      </Stack>

      <Stack direction={'row'} gap={2} alignItems={'center'} width={'100%'}>
        <span className={'min-w-[150px]'}>Service Provider</span>
        <Autocomplete
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.2)',
            },
          }}
          size={'small'}
          fullWidth={true}
          PopperComponent={LargePopper}
          onOpen={() => {
            fetchServiceProviders();
          }}
          getOptionLabel={(option: any) => option?.name}
          options={serviceProviderOptions}
          renderInput={(params) => <TextField {...params} label="" />}
          onChange={(event, newValue) => {
            setSelectedServiceProvider(newValue);
          }}
        />
      </Stack>

      {loading ? (
        <>
          <Skeleton
            sx={{
              mt: '20px',
            }}
            variant="rounded"
            width={'100%'}
            height={350}
          />
        </>
      ) : (
        <>
          {properties.map((venueItem: any) => {
            return (
              <Stack
                component="section"
                direction={'row'}
                sx={{
                  p: '20px',
                  background: '#fff',
                  borderRadius: '10px',
                  mt: '20px',
                  width: '100%',
                }}
              >
                <Stack width={'100%'}>
                  <Stack
                    direction={'row'}
                    gap={0}
                    sx={{
                      '& > span': {},
                    }}
                  >
                    <span className={''}>
                      <PersonIcon
                        sx={{
                          color: '#256861',
                        }}
                      />{' '}
                      <span className={'text-[#3d3d3d]'}>
                        Name: {venueItem?.name}
                      </span>
                    </span>
                  </Stack>
                  <Stack
                    direction={'row'}
                    gap={0}
                    mt={'10px'}
                    sx={{
                      '& > span': {},
                    }}
                  >
                    <span className={''}>
                      <RoomIcon
                        sx={{
                          color: '#256861',
                        }}
                      />{' '}
                      <span className={'text-[#3d3d3d]'}>
                        Address: {venueItem?.address_line1}
                      </span>
                    </span>
                  </Stack>
                  <Stack
                    direction={'row'}
                    gap={0}
                    mt={'10px'}
                    sx={{
                      '& > span': {},
                    }}
                  >
                    <span className={''}>
                      <GpsFixedIcon
                        sx={{
                          color: '#256861',
                        }}
                      />{' '}
                      <span className={'text-[#3d3d3d]'}>
                        Coordinates: {venueItem?.coordinates}
                      </span>
                    </span>
                  </Stack>
                  <Stack
                    direction={'row'}
                    gap={0}
                    mt={'10px'}
                    sx={{
                      '& > span': {},
                    }}
                  >
                    <span className={''}>
                      <ManageAccountsRoundedIcon
                        sx={{
                          color: '#256861',
                        }}
                      />{' '}
                      <span className={'text-[#3d3d3d]'}>
                        Service Provider: {venueItem?.service_provider?.name}
                      </span>
                    </span>
                  </Stack>

                  <Stack mt={'20px'}>
                    <MapGoogle coordinates={[]} address={venueItem} />
                  </Stack>
                </Stack>
                <Stack>
                  <MoreOptions
                    clickOnEdit={() => {
                      setOpenedSlidingPane({
                        schema: venue,
                        row: venueItem,
                      });
                    }}
                    clickOnDelete={() => {
                      setOpenConfirmDialog({
                        row: venueItem,
                        schema: venue,
                      });
                    }}
                  />
                </Stack>
              </Stack>
            );
          })}
        </>
      )}

      {currentAddNewPaneEntity?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={currentAddNewPaneEntity?.schema}
            mainSchema={schema}
            id={currentAddNewPaneEntity?.row?.id}
            hasSlidingPane={false}
            onSubmit={(result: any) => {
              fetchVenues();
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
            propertiesFromTable={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            row={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            onClose={(value) => {
              fetchVenues();
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
          />
        </>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;

                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, { id: deletedRow.id })
                  .then((res: any) => {
                    fetchVenues();
                    toaster.show({
                      intent: 'success',
                      message: `Successfully deleted ${address.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                    });
                    setOpenConfirmDialog(false);
                  })
                  .catch((e: any) => {
                    toaster.show({
                      icon: 'error',
                      message: `Error: Unable to delete ${address.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                      intent: 'danger',
                    });
                  });
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchVenues();
            setOpenedSlidingPane({});
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            fetchVenues();
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
